import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const projects = [
  {
    id: 1,
    title: "Iron & Ivory Barbershop",
    category: "Web Design",
    description: "A moody, editorial website for an Oakland barbershop. Vintage-modern with real craft energy.",
    color: "bg-[#FE4A90]",
    image: "https://images.unsplash.com/photo-1572663459735-75425e957ab9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW9vZHklMjBiYXJiZXJzaG9wJTIwaW50ZXJpb3IlMjB2aW50YWdlfGVufDB8fHx8MTc3NjI4ODE1Mnww&ixlib=rb-4.1.0&q=85",
    alt: "Iron & Ivory Barbershop website design by Corcoran Design",
    span: "lg:col-span-7",
    link: "/work/iron-ivory",
  },
  {
    id: 2,
    title: "Summit Roofing",
    category: "Web Design",
    description: "Bold, trust-building website for a local roofing contractor. Built to generate leads.",
    color: "bg-[#FFD166]",
    image: "https://images.unsplash.com/photo-1686460980644-14b8c9f42021?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxiZWF1dGlmdWwlMjBtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yJTIwcm9vZmluZ3xlbnwwfHx8fDE3NzYyMTI1MDR8MA&ixlib=rb-4.1.0&q=85",
    alt: "Summit Roofing contractor website design by Corcoran Design",
    span: "lg:col-span-5",
    link: "/work/summit-roofing",
  },
  {
    id: 3,
    title: "GreenLine Landscaping",
    category: "Web Design",
    description: "Premium, nature-forward website for a landscaping company. Clean, airy, and architect-level polish.",
    color: "bg-[#06D6A0]",
    image: "https://images.unsplash.com/photo-1761637824120-cc56577081cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxiZWF1dGlmdWwlMjBtYWludGFpbmVkJTIwZ2FyZGVuJTIwbGFuZHNjYXBlJTIwZGVzaWduJTIwbHV4dXJ5fGVufDB8fHx8MTc3NjI4OTE1OXww&ixlib=rb-4.1.0&q=85",
    alt: "GreenLine Landscaping company website design by Corcoran Design",
    span: "lg:col-span-5",
    link: "/work/greenline",
  },
  {
    id: 4,
    title: "Threshold Studio",
    category: "Web Design",
    description: "Gallery-like portfolio for an architecture studio. Minimal, asymmetric, and confidently quiet.",
    color: "bg-[#FFFDF5]",
    image: "https://images.unsplash.com/photo-1630908650794-e76619014675?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZyUyMGNvbmNyZXRlJTIwbWluaW1hbHxlbnwwfHx8fDE3NzYyODk4MDd8MA&ixlib=rb-4.1.0&q=85",
    alt: "Threshold Studio architecture firm website design by Corcoran Design",
    span: "lg:col-span-7",
    link: "/work/threshold-studio",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.12,
      duration: 0.5,
      ease: [0.34, 1.56, 0.64, 1],
    },
  }),
};

export default function Portfolio() {
  return (
    <section
      id="work"
      data-testid="portfolio-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#FFFDF5]"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <p className="font-mono text-sm uppercase tracking-widest font-bold text-[#FE4A90] mb-3">
          Selected Work
        </p>
        <h2
          data-testid="portfolio-title"
          className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A0A0A] uppercase"
        >
          Projects
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 mt-14">
        {projects.map((project, i) => (
          <motion.div
            key={project.id}
            custom={i}
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className={`${project.span}`}
          >
            <Link to={project.link} data-testid={`portfolio-card-${project.id}`}>
              <div
                className={`brutal-card ${project.color} p-4 md:p-5 group cursor-pointer h-full`}
              >
                <div className="overflow-hidden border-2 border-[#0A0A0A]">
                  <img
                    src={project.image}
                    alt={project.alt}
                    className="w-full h-48 md:h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="mt-4 flex items-start justify-between">
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest font-bold text-[#0A0A0A] opacity-60">
                      {project.category}
                    </p>
                    <h3 className="font-heading text-xl sm:text-2xl text-[#0A0A0A] mt-1">
                      {project.title}
                    </h3>
                    <p className="font-body text-sm text-[#525252] mt-2 max-w-md">
                      {project.description}
                    </p>
                  </div>
                  <ExternalLink
                    size={20}
                    className="text-[#0A0A0A] opacity-40 group-hover:opacity-100 transition-opacity mt-1 flex-shrink-0"
                  />
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
